import { useEffect } from "react";
import { pubSub } from "../ably";
import { CHANNELS, SESSION_DETAILS_HISTORY } from "../channel/channels.js";
import { setSessionDetails } from "./adminStore";
import { queryClient } from "../api/queryClient.js";
import { buildSessionCacheKey, createCmeCommonChannelConfig } from "../realtime/channelRegistry.js";
import { applyChannelMessage, resetChannelSyncState } from "../realtime/channelSyncEngine.js";

let suppressUntil = 0;
export function suppressSettingEcho() {
  suppressUntil = Date.now() + 2000;
}
function shouldSuppressEcho() {
  return Date.now() < suppressUntil;
}
function clearSuppressEcho() {
  suppressUntil = 0;
}

function isOnlySettingPayload(raw) {
  const onlySettingTop = raw && typeof raw === "object" && Object.keys(raw).length === 1 && "setting" in raw && raw.setting && typeof raw.setting === "object";
  const onlySettingWrapped = raw && typeof raw === "object" && raw.data && typeof raw.data === "object" && Object.keys(raw.data).length === 1 && "setting" in raw.data && raw.data.setting && typeof raw.data.setting === "object";
  return onlySettingTop || onlySettingWrapped;
}

// Live updates only arrive as new Ably messages come in - if nothing's
// pushed anything for a while, the cache can lag behind whatever CME
// actually has right now. This forces a fresh check: re-pull recent
// channel history and replay it through the same merge logic the live
// handler uses, then hand back the resulting up-to-date session snapshot
// directly (not via React state, which needs a re-render to catch up).
export async function refreshCmeSessionOnce(sessionId) {
  if (!sessionId) return null;
  const cacheKey = buildSessionCacheKey(sessionId);
  const config = createCmeCommonChannelConfig(sessionId);
  const items = await pubSub.getChannelHistoryWithMeta(CHANNELS.SESSION_DETAILS, SESSION_DETAILS_HISTORY);
  // History replay must be authoritative - clear stale LWW state so newest cmeall status wins
  resetChannelSyncState(cacheKey);
  // Ably returns newest-first; replay oldest-first so the real order wins.
  [...items].reverse().forEach((item) => {
    applyChannelMessage({ queryClient, cacheKey, config, message: { id: item.id, timestamp: item.timestamp, raw: item.data } });
  });
  return queryClient.getQueryData(cacheKey);
}

export default function useCmeSessionSync(sessionId) {
  useEffect(() => {
    if (!sessionId) return;
    const cacheKey = buildSessionCacheKey(sessionId);
    const config = createCmeCommonChannelConfig(sessionId);

    const handler = (data, _isHistory, meta = {}) => {
      if (isOnlySettingPayload(data) && shouldSuppressEcho()) {
        clearSuppressEcho();
        return;
      }
      applyChannelMessage({
        queryClient,
        cacheKey,
        config,
        message: { id: meta.id, timestamp: meta.timestamp, raw: data },
      });
    };

    pubSub.subscribe(CHANNELS.SESSION_DETAILS, handler);
    return () => pubSub.unsubscribe(CHANNELS.SESSION_DETAILS, handler);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const cacheKey = buildSessionCacheKey(sessionId);
    const mirror = () => {
      const data = queryClient.getQueryData(cacheKey);
      if (data) setSessionDetails(sessionId, data);
    };
    mirror();
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event?.query?.queryKey;
      if (!Array.isArray(key) || key[0] !== cacheKey[0] || Number(key[1]) !== Number(cacheKey[1])) return;
      mirror();
    });
    return unsubscribe;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const onConn = (connected) => {
      if (!connected) console.warn("[useCmeSessionSync] Ably not connected — waiting for channel data");
    };
    pubSub.onConnectionChange(onConn);
    return () => pubSub.offConnectionChange(onConn);
  }, [sessionId]);
}
